<template>
  <div class="mb-4 list-options mb-sm-2">
    <v-card outlined>
      <v-card-text
        class="flex-wrap w-full d-flex pa-0"
        :class="{
          'justify-space-between':($vuetify.breakpoint.mdAndUp || $vuetify.breakpoint.xs),
          'justify-center':$vuetify.breakpoint.sm,
        }"
      >
        <div
          class="d-flex"
          :class="{'w-full justify-space-between flex-column':$vuetify.breakpoint.xs} "
        >
          <!-- Search -->
          <div
            class="pa-2 flex-grow-1 lead-list__search"
            outlined
          >
            <v-text-field
              v-model="listOptions.search"
              class="mt-2"
              :label="$t('labels.leads.lead-search')"
              hide-details
              single-line
              clearable
              solo
              flat
              dense
              outlined
              append-icon="mdi-magnify"
            />
          </div>

          <!-- Filter -->
          <div
            v-if="!isPlatformMarkero"
            class="d-flex align-center px-2 option-container"
            :class="{'w-full':$vuetify.breakpoint.xs}"
          >
            <v-badge
              :value="activeFiltersCount > 0"
              :content="activeFiltersCount"
              color="red"
              overlap
              :class="{'w-full':$vuetify.breakpoint.xs}"

              right
            >
              <v-btn
                outlined
                color="primary"
                class="camel-case option-btn"
                @click="$emit('toggleLeadListTool', 'filter')"
              >
                <v-icon left>
                  mdi-filter-variant
                </v-icon>
                {{ $t('labels.filter') }}
              </v-btn>
            </v-badge>
          </div>
        </div>

        <!-- Expanded Actions for Desktop -->
        <div
          class="d-flex align-center"
          :class="{'w-full justify-space-between flex-column':$vuetify.breakpoint.xs} "
        >
          <!-- Create new Lead -->
          <Permission permission="lead:create">
            <CreateLeadDialog
              class="px-2"
              :refetch-parent="refetchParent"
            >
              <v-btn
                color="primary"
                class="camel-case w-full"
              >
                <v-icon
                  small
                  left
                >
                  mdi-account-plus
                </v-icon>
                {{ $t('buttons.leads.create') }}
              </v-btn>
            </CreateLeadDialog>
          </Permission>

          <!-- Import -->
          <Permission permission="lead:import">
            <Feature :feature-slug="featureNames.LEAD_IMPORT">
              <v-btn
                v-if="$vuetify.breakpoint.mdAndUp"
                color="primary"
                class="camel-case mx-2"
                @click="$emit('showImport')"
              >
                <v-icon
                  small
                  left
                >
                  mdi-upload
                </v-icon>
                {{ $t('buttons.leads.import') }}
              </v-btn>
            </Feature>
          </Permission>

          <!-- Acquisition Booster -->
          <Permission permission="lead:invite-bulk">
            <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
              <AcquisitionBoosterButton
                :is-disabled="mailProvider !== 'HOPPERMATION'"
                icon="mdi-rocket-launch-outline"
                :label="$t('labels.acquisition-booster.name')"
                @click="$emit('toggleLeadListTool', 'invitation')"
              />
            </Feature>
          </Permission>
        </div>

        <!-- Actions -->
        <div
          class="justify-end d-flex align-center"
          :class="{'w-full justify-space-between flex-column':$vuetify.breakpoint.xs} "
        >
          <!-- More Functions -->
          <div
            v-if="$vuetify.breakpoint.lgAndDown"
            class="px-2 option-container d-flex align-center"
            :class="{'w-full':$vuetify.breakpoint.xs}"
          >
            <v-menu
              bottom
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="px-2 camel-case option-btn"

                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>
                    mdi-dots-vertical
                  </v-icon>
                  {{ $t('labels.more') }}
                </v-btn>
              </template>
              <v-list>
                <Permission permission="lead:export-bulk">
                  <v-list-item class="list-item-container">
                    <Feature :feature-slug="featureNames.LEAD_EXPORT">
                      <Export @exportLeads="(value) => $emit('exportLeads', value)" />
                    </feature>
                  </v-list-item>
                </Permission>

                <v-list-item class="list-item-container">
                  <ColumnSettingsButton
                    icon="mdi-view-column"
                    :label="$t('labels.display')"
                    @click="$emit('toggleLeadListTool', 'column')"
                  />
                </v-list-item>

                <Permission permission="lead:delete-bulk">
                  <Feature :feature-slug="featureNames.DELETE_LEADS">
                    <v-list-item class="list-item-container">
                      <ColumnSettingsButton
                        icon="mdi-delete"
                        :label="$t('labels.delete')"
                        @click="$emit('toggleLeadListTool', 'deleteLeads')"
                      />
                    </v-list-item>
                  </Feature>
                </Permission>

                <Permission permission="lead:assign-bulk">
                  <v-list-item class="list-item-container">
                    <ColumnSettingsButton
                      icon="mdi-account-arrow-right-outline"
                      :label="$t('labels.assign')"
                      @click="$emit('toggleLeadListTool', 'assignLeads')"
                    />
                  </v-list-item>
                </Permission>
              </v-list>
            </v-menu>
          </div>

          <div
            v-else
            class="d-flex"
          >
            <Permission permission="lead:export-bulk">
              <Feature :feature-slug="featureNames.LEAD_EXPORT">
                <Export @exportLeads="(value) => $emit('exportLeads', value)" />
              </feature>
            </Permission>

            <!-- Column Settings -->
            <ColumnSettingsButton
              icon="mdi-view-column"
              :label="$t('labels.display')"
              @click="$emit('toggleLeadListTool', 'column')"
            />

            <Permission permission="lead:delete-bulk">
              <Feature :feature-slug="featureNames.DELETE_LEADS">
                <ColumnSettingsButton
                  icon="mdi-delete"
                  :label="$t('labels.delete')"
                  @click="$emit('toggleLeadListTool', 'deleteLeads')"
                />
              </Feature>
            </Permission>
            <Permission permission="lead:assign-bulk">
              <ColumnSettingsButton
                icon="mdi-account-arrow-right-outline"
                :label="$t('labels.assign')"
                @click="$emit('toggleLeadListTool', 'assignLeads')"
              />
            </Permission>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import currencyMixin from '@/mixins/currency'
import featureMixin from '@/mixins/feature'
import MAIL_PROVIDER from '../queries/MailProvider.gql'
import brandingMixin from '@/mixins/branding'
import AcquisitionBoosterButton from './AcquisitionBoosterButton.vue'
import CreateLeadDialog from './CreateLeadDialog.vue'
import ColumnSettingsButton from './ColumnSettingsButton.vue'
import Permission from '@/components/Permission'

export default {
  components: {
    Export: () => import('../Export'),
    AcquisitionBoosterButton,
    CreateLeadDialog,
    ColumnSettingsButton,
    Permission
  },

  mixins: [currencyMixin, brandingMixin, featureMixin],

  props: {
    listOptions: {
      type: Object,
      required: true
    },
    activeFiltersCount: {
      type: Number,
      required: true
    },
    refetchParent: {
      type: Function,
      required: true
    }
  },

  apollo: {
    mailProvider: {
      query: MAIL_PROVIDER,
      variables () {
        return {
          companyId: this.$auth.user.companyId
        }
      },
      update: (data) => data.company.mailProvider
    }
  }
}
</script>

<style>

.option-container{
  height: 50px;
}

.option-btn  {
  width: 100%;
}

.option-btn .v-btn__content {
  padding-left: 10px;
  padding-right: 10px;
}

/* breakpoint md and up */
@media (min-width: 600px) {

  .lead-list__search{
    min-width: 350px;
    max-width: 350px;
  }

  .option-container {
    max-width: 120px;
    height: 100%;
  }
}

.camel-case {
    text-transform: unset !important;
    font-weight: normal;
}

.list-options .v-input--selection-controls .v-input__slot, .v-input--select{
  margin-bottom: 0px;
}

.list-options .v-text-field.v-text-field--enclosed{
  margin-top:0px !important;
}

.activate-window-step{
  max-width: 600px
}

.list-item-container{
  padding: 0px;
}

.list-item-container:hover{
  background-color: rgb(245, 245, 245);
}

.menu-list-item{
  padding-left: 15px;
  padding-right: 15px;
  height: 48px;
  display:flex;
  align-items:center;
  cursor: pointer;
}

.filter-container{
  position:relative;
}

</style>
